import {
  batchCreateRelationship,
  batchReplaceSuperElement,
  batchDeleteElementAndSubs,
  getNestedOrFloatingElementById,
  getNextUuid,
  batchMovePersonToOtherRecord,
  getNextArkId,
  batchCreatePerson,
  createFieldValue,
  batchCreateRecord,
  getNestedSubelementsByType,
} from '@fs/zion-sls'
import zionDebug from '@fs/zion-debug'
import { superDeduper } from '../../../../views/indexing/components/editPanels/utils/editPanelDispatchBuilders'
import { preparePersonIdsAndPartitionKeys } from './relationshipKeys'
import { restoreRelationshipsAmongMovedPersons } from './restoreRelationships'
import { MAKE_NEW_RECORD, NO_RELATION_TYPE } from './relationshipConstants'

// To turn this on, in the browser console: localStorage.debug = 'fs:rip:saveRelationships'
const debug = zionDebug('rip:saveRelationships')

/**
 * Moves a person to the target record if they are not already in it.
 * Removes the person from their parent record if they have a different superElement than the target,
 * and adds them to the specified record.
 *
 * **NOTE:** This removes all relationships from the person that is moved.
 *
 * @param {Object} person - The person object to move
 * @param {string} targetRecordId - The ID of the destination record
 * @param {string} targetRecPartitionKey - Partition key of the destination record, which is not always
 *   the current image: a group can be recorded on another image and still hold people from this one
 * @returns {Array} An array of dispatch actions
 */
function movePersonToTargetRecord({ person, targetRecordId, targetRecPartitionKey }) {
  let dispatches = []

  if (!targetRecordId) throw new Error('Target record ID is required to move a person into a record.')

  // Check if person needs to be moved
  const personIsFloating = !person.recordId

  // Getting source record partition key this way mostly because of dangling persons
  const sourceRecPartitionKey =
    person?.superElements?.find((se) => se.id === person.recordId)?.partitionKey || person?.partitionKey

  const isDifferentRecord = person?.recordId !== targetRecordId
  if ((personIsFloating || isDifferentRecord) && sourceRecPartitionKey) {
    dispatches = dispatches.concat(
      batchMovePersonToOtherRecord({
        person,
        destRecordId: targetRecordId,
        sourceRecPartitionKey,
        destRecPartitionKey: targetRecPartitionKey,
      })
    )
  }

  return dispatches
}

/**
 * Validates that form relationships don't contain duplicates (multiple relationships with the same person)
 *
 * @param {Object} params
 * @param {Array<Object>} params.relationships - Form relationship data
 * @throws {Error} If duplicate relationships are found
 */
function validateNoDuplicateRelationships({ relationships }) {
  const otherPersonCounts = {}

  relationships.forEach((relationship) => {
    const personId = relationship.personId

    // Skip validation if no other person is set (empty/incomplete row) or if relationship is deleted
    if (!personId || !relationship.relationshipType || relationship.isDeleted) {
      return
    }

    if (otherPersonCounts[personId]) {
      otherPersonCounts[personId]++
    } else {
      otherPersonCounts[personId] = 1
    }
  })

  const duplicates = Object.entries(otherPersonCounts).filter(([, count]) => count > 1)

  if (duplicates.length > 0) {
    // This should be a user-friendly message explaining that two people can only have one relationship
    throw new Error(
      'Cannot save: A person can only have one relationship type with another person. Please remove duplicate relationships before saving.'
    )
  }
}

/**
 * Validates that default values correspond to actual SLS relationships
 * If a relationship ID exists in default values but not in SLS, this indicates
 * a data loading error that should be caught.
 *
 * @param {Object} params
 * @param {Object} params.defaultValues - Default form values containing relationship IDs
 * @param {Object} params.images - SLS images data
 * @throws {Error} If default relationship ID doesn't exist in SLS
 */
function validateDefaultValuesMatchSls({ defaultValues, images }) {
  if (!defaultValues?.relationshipsById) {
    return
  }

  const defaultValueKeys = Object.keys(defaultValues.relationshipsById)

  defaultValueKeys.forEach((key) => {
    const slsRelationship = getNestedOrFloatingElementById(images, key)

    if (!slsRelationship) {
      throw new Error(
        `Data integrity error: Default form value contains relationship ID "${key}" which does not exist in SLS state. ` +
          `This indicates a problem loading relationship data into the form.`
      )
    }
  })
}

/**
 * Checks if a relationship type has changed from its default value
 *
 * @param {Object} params
 * @param {Object} params.relationship - Current form relationship
 * @param {Object} params.defaultValues - Default form values
 * @returns {boolean} True if relationship type changed
 */
function hasRelationshipTypeChanged({ relationship, defaultValues }) {
  if (!relationship.slsRelationshipId || !defaultValues.relationshipsById) {
    return false
  }

  const defaultRel = defaultValues.relationshipsById[relationship.slsRelationshipId]
  if (!defaultRel) {
    return false
  }

  // Compare both relationshipType and relationshipDirection to detect relationship type changes
  const result =
    defaultRel.relKey !== relationship.relationshipType || defaultRel.order !== relationship.relationshipDirection
  return result
}

/**
 * CASE 1: Handles existing relationships that have been modified.
 * - If relationship type changed: delete old and create new
 * - If other person changed: replace super element
 * - Otherwise: no action needed
 *
 * @param {Object} params
 * @param {Object} params.relationship - Current form relationship
 * @param {Object} params.relationshipEl - Existing SLS relationship element
 * @param {Object} params.person - Primary person object
 * @param {Object} params.otherPerson - Related person object
 * @param {Object} params.defaultValues - Default form values
 * @param {Object} params.images - SLS images data
 * @param {string} params.targetRecordId - Target record ID to ensure both persons are in the same record
 * @param {string} params.targetRecPartitionKey - Partition key of the target record
 * @returns {Array<Object>} Array of SLS dispatches for this relationship
 */
function handleExistingRelationship({
  relationship,
  relationshipEl,
  person,
  otherPerson,
  defaultValues,
  targetRecordId,
  targetRecPartitionKey,
}) {
  let dispatches = []

  // Check if relationship type changed (relationshipType or relationshipDirection)
  if (hasRelationshipTypeChanged({ relationship, defaultValues })) {
    // Relationship type changed: Delete old relationship and create new one
    // This avoids conflicts with SLS backend validation
    dispatches = dispatches.concat(batchDeleteElementAndSubs({ element: relationshipEl }))

    // If the relationship is being changed to NO_RELATION, we do not create a new relationship, just return the delete dispatches
    if (relationship?.relationship?.key === NO_RELATION_TYPE) {
      return dispatches
    }

    // Create new relationship with new type and new ID
    const { person1Id, person2Id, person1PartitionKey, person2PartitionKey } = preparePersonIdsAndPartitionKeys({
      person,
      otherPerson,
      relationshipDirection: relationship.relationshipDirection,
    })

    const newRelationshipId = getNextUuid()

    // CRITICAL ORDER: Person moves MUST happen before creating the relationship
    const ensurePersonsInRecordDispatches = [
      ...movePersonToTargetRecord({ person, targetRecordId, targetRecPartitionKey }),
      ...movePersonToTargetRecord({ person: otherPerson, targetRecordId, targetRecPartitionKey }),
    ]

    const createRelationshipDispatches = batchCreateRelationship({
      partitionKey: person.partitionKey,
      person1ID: person1Id,
      person2ID: person2Id,
      person1PartitionKey,
      person2PartitionKey,
      relType: relationship.relationshipType,
      id: newRelationshipId,
    })

    // CRITICAL: Append person move dispatches FIRST, then relationship creation dispatches
    dispatches = dispatches.concat(ensurePersonsInRecordDispatches)
    dispatches = dispatches.concat(createRelationshipDispatches)
  } else {
    // Relationship type unchanged, check if other person changed
    const otherPersonSuperEl = relationshipEl.superElements?.find((superEl) => superEl.id !== person.id)

    if (!otherPersonSuperEl) {
      throw new Error(
        `Relationship "${relationship.slsRelationshipId}" is missing the other person's super element. ` +
          `Expected to find a super element that is not "${person.id}".`
      )
    }

    const currentOtherPersonId = otherPersonSuperEl.id

    if (currentOtherPersonId !== relationship.personId) {
      // CRITICAL ORDER: Person moves MUST happen before replacing the relationship's super element
      const ensurePersonsInRecordDispatches = [
        ...movePersonToTargetRecord({ person, targetRecordId, targetRecPartitionKey }),
        ...movePersonToTargetRecord({ person: otherPerson, targetRecordId, targetRecPartitionKey }),
      ]

      const replaceSuperElementDispatches = batchReplaceSuperElement({
        oldSuperPartitionKey: otherPersonSuperEl.partitionKey,
        newSuperPartitionKey: otherPerson.partitionKey,
        oldSuperId: currentOtherPersonId,
        newSuperId: relationship.personId,
        subId: relationship.slsRelationshipId,
        oldElementType: 'PERSON',
        newElementType: 'PERSON',
      })

      // CRITICAL: Append person move dispatches FIRST, then super element replacement
      dispatches = dispatches.concat(ensurePersonsInRecordDispatches)
      dispatches = dispatches.concat(replaceSuperElementDispatches)
    }
    // If neither type nor person changed, no action needed
  }

  return dispatches
}

/**
 * CASE 2: Creates a new relationship that doesn't exist in SLS.
 *
 * @param {Object} params
 * @param {Object} params.relationship - Current form relationship
 * @param {Object} params.person - Primary person object
 * @param {Object} params.otherPerson - Related person object
 * @param {Object} params.images - SLS images data
 * @param {string} params.targetRecordId - Target record ID to ensure both persons are in the same record
 * @param {string} params.targetRecPartitionKey - Partition key of the target record
 * @param {boolean} [params.personMovedToNewRecord=false] - Indicates if the focused person was moved to a new record to avoid creating redundant move dispatches
 * @returns {Array<Object>} Array of SLS dispatches for this relationship
 */
function handleNewRelationship({
  relationship,
  person,
  otherPerson,
  targetRecordId,
  targetRecPartitionKey,
  personMovedToNewRecord = false,
}) {
  let dispatches = []

  // Generate new ID if not present
  const newRelationshipId = relationship.slsRelationshipId || getNextUuid()

  const { person1Id, person2Id, person1PartitionKey, person2PartitionKey } = preparePersonIdsAndPartitionKeys({
    person,
    otherPerson,
    relationshipDirection: relationship.relationshipDirection,
  })

  const ensurePersonsInRecordDispatches = []

  // CRITICAL ORDER: Person moves MUST happen before creating the relationship
  // Relationships can only be created between persons that are in the same record

  // If the focused person was already moved to a new record earlier, skip creating redundant move dispatches
  if (!personMovedToNewRecord) {
    ensurePersonsInRecordDispatches.push(...movePersonToTargetRecord({ person, targetRecordId, targetRecPartitionKey }))
  }

  // Always check/move the other person to the target record
  ensurePersonsInRecordDispatches.push(
    ...movePersonToTargetRecord({ person: otherPerson, targetRecordId, targetRecPartitionKey })
  )

  // If the relationship is new, we still need to ensure the recordId is set for persons being moved to the target record
  if (relationship?.isNew) {
    ensurePersonsInRecordDispatches.push({ type: 'SET_RECORD_PERSON_IDS', value: targetRecordId })
  }

  // If relationship type is NO_RELATION, skip relationship creation and just move the person to the target record (if needed)
  if (relationship?.relationship?.key === NO_RELATION_TYPE) {
    return ensurePersonsInRecordDispatches
  }

  const createRelationshipDispatches = batchCreateRelationship({
    partitionKey: person.partitionKey,
    person1ID: person1Id,
    person2ID: person2Id,
    person1PartitionKey,
    person2PartitionKey,
    relType: relationship.relationshipType,
    id: newRelationshipId,
  })

  // CRITICAL: Append person move dispatches FIRST, then relationship creation dispatches
  // This ensures persons are in the same record before the relationship is created
  dispatches = dispatches.concat(ensurePersonsInRecordDispatches)
  dispatches = dispatches.concat(createRelationshipDispatches)

  return dispatches
}

/**
 * The people this save moves into the target record, resolved from SLS.
 *
 * Mirrors the condition inside {@link movePersonToTargetRecord}, so this is exactly the set whose
 * relationships the moves are about to delete.
 *
 * @param {Object} params
 * @param {Array<Object>} params.relationships - Current form relationships
 * @param {Object} params.person - Primary person object, who is governed by the form and excluded
 * @param {Object} params.images - SLS images data
 * @param {string} params.targetRecordId - Record everybody is being gathered into
 * @returns {Array<Object>} SLS person objects that will actually move
 */
function getPersonsMovingIntoRecord({ relationships, person, images, targetRecordId }) {
  return relationships
    .filter((relationship) => !relationship.isDeleted && relationship.relationshipType && relationship.personId)
    .map((relationship) => getNestedOrFloatingElementById(images, relationship.personId))
    .filter(
      (otherPerson) =>
        otherPerson &&
        otherPerson.id !== person.id &&
        (!otherPerson.recordId || otherPerson.recordId !== targetRecordId)
    )
}

/**
 * CASE 3: Handles deleted members from household.
 * Any relationship in default values that's not in the current form should be deleted.
 *
 * @param {Object} params
 * @param {Array<Object>} params.relationship - Current form relationship
 * @returns {Array<Object>} Array of SLS dispatches for moving the person to a new record
 */
async function handleRemovePersonFromHousehold({ relationship, images, currentPartitionKey }) {
  const dispatches = []
  const person = getNestedOrFloatingElementById(images, relationship.personId)

  if (person) {
    // Create a new record for the person being removed from the household
    const newRecordId = await getNextArkId('record')
    const createRecordDispatches = batchCreateRecord({
      imagePartitionKey: currentPartitionKey,
      recordArk: newRecordId,
    })
    dispatches.push(...createRecordDispatches)

    // Remove person from current record and move to new record (this also removes all relationships)
    const movePersonDispatches = batchMovePersonToOtherRecord({
      person,
      destRecordId: newRecordId,
      sourceRecPartitionKey: person.partitionKey,
      destRecPartitionKey: currentPartitionKey,
    })
    dispatches.push(...movePersonDispatches)
  }

  return dispatches
}

/**
 * Filters out relationships that have the type NO_RELATION_TYPE. To use within Array.filter().
 *
 * @param {Object} rel - The relationship object to check.
 * @param {string} rel.relationship.key - The relationship type key.
 * @returns {boolean} Returns true if the relationship type is not NO_RELATION_TYPE, otherwise false.
 */
export const filterNoRelation = (rel) => rel?.relationship?.key !== NO_RELATION_TYPE

/**
 * Generate SLS dispatches for relationship changes
 *
 * This function implements the following logic:
 * 1. Validates no duplicate relationships (same person appears multiple times)
 * 2. Validates default values correspond to actual SLS data
 * 3. For existing relationships:
 *    - Ensures persons are in the same record, creating a new record if necessary
 *    - If relationship type changed: delete old and create new
 *    - If other person changed: replace super element
 *    - Otherwise: no action needed
 * 4. For new relationships: create with generated ID
 * 5. For deleted relationships: delete from SLS
 *
 * @param {Object} params
 * @param {Object} params.formData - Form data containing relationships and target record information
 * @param {Object} params.defaultValues - Default form values (relationships that existed when form loaded)
 * @param {Object} params.person - Primary person object
 * @param {Object} params.images - SLS images data
 * @param {string} [params.appName] - Application name for attribution
 * @param {Array<Object>} [params.reviewDispatches] - Additional dispatches from review process to include
 * @param {string} [params.currentPartitionKey] - Current image partition key
 * @returns {Promise<Array<Object>>} Array of SLS dispatches
 * @throws {Error} If validation fails or data integrity issues detected
 */
export default async function getRelationshipDispatches({
  formData,
  defaultValues,
  person,
  images,
  appName,
  reviewDispatches = [],
  currentPartitionKey,
}) {
  const recordDispatches = []
  let dispatches = []
  let personMovedToNewRecord = false

  const relationships = formData.relationships
  const defaultRelationships = defaultValues.relationships

  // Extract relationships and target record from form data,
  const originalTargetRecordId = defaultValues.targetRecordId
  const targetRecordId = formData.targetRecordId || person.recordId
  if (!targetRecordId)
    throw new Error(
      'Attempting to save relationships without a reference to the record within which the persons exist.'
    )

  // The group can be recorded on an image other than the one being viewed, so the destination
  // partition comes from the chosen household rather than from the current image. Falls back to the
  // current image for a record that does not exist yet, which is always created on it.
  const targetRecPartitionKey = formData?.household?.partitionKey || currentPartitionKey

  // Use relationshipsById from form data if available, otherwise fall back to defaultValues
  const relationshipsById = formData.relationshipsById || defaultValues?.relationshipsById || {}
  const processedDefaultValues = { ...defaultValues, relationshipsById, relationships: defaultRelationships }

  // Handling when a person is being moved to a different record
  if (originalTargetRecordId !== targetRecordId) {
    // We still need to move the person even if the target is MAKE_NEW_RECORD
    personMovedToNewRecord = originalTargetRecordId !== targetRecordId && originalTargetRecordId !== MAKE_NEW_RECORD

    // Check if that person has existing relationships that need to be removed as well
    const previousRelationships = getNestedSubelementsByType(person, 'RELATIONSHIP') || []

    // Remove previous relationships
    previousRelationships.forEach((relationship) => {
      dispatches = dispatches.concat(batchDeleteElementAndSubs({ element: relationship }))
    })

    // The record partition key for the new record, it can be on another image so we get it from the form data
    const newRecordPartitionKey = formData?.household?.partitionKey

    // Defensive check
    if (!newRecordPartitionKey) {
      throw new Error('New record partition key is required when moving a person to a different record')
    }

    // Move person to target record
    recordDispatches.push(
      ...batchMovePersonToOtherRecord({
        person,
        destRecordId: targetRecordId,
        sourceRecPartitionKey: person.partitionKey,
        destRecPartitionKey: newRecordPartitionKey,
      })
    )
  }

  // Defensive validation
  if (!person?.id) {
    throw new Error('Primary person must have an ID')
  }

  if (!images) {
    throw new Error('SLS images data is required')
  }

  // Validate no duplicate relationships
  validateNoDuplicateRelationships({ relationships })

  // Validate default values match SLS state
  validateDefaultValuesMatchSls({ defaultValues: processedDefaultValues, images })

  // Map to track newly created person IDs, will use them later to update review relationships
  const newPersonIdsMap = new Map()

  // Process each relationship from the form (CASE 1 & CASE 2)
  const relationshipResults = await Promise.all(
    relationships.map(async (relationship) => {
      let localDispatches = []

      // Defensive check: ensure relationship has required data
      // Skip incomplete/empty relationship rows or deleted relationships
      // Also skip relationships with NO_RELATION_TYPE that are not new (We'll handle deletions separately)
      if (!relationship.relationshipType || relationship.isDeleted) {
        return []
      }

      // Handle New Person Creation
      if (relationship.value?.key === 'NEW_PERSON' && relationship.newPerson) {
        const newPersonId = await getNextArkId('person')
        newPersonIdsMap.set(relationship.personId, newPersonId)
        const partitionKey = person.partitionKey
        const attr = { comment: '', reasonId: '', app: appName }

        const givenNameValue = createFieldValue(relationship.newPerson.givenName, attr)
        const surnameValue = createFieldValue(relationship.newPerson.surname, attr)

        const personDispatches = batchCreatePerson({
          partitionKey,
          personId: newPersonId,
          recordId: targetRecordId,
          givenNameFieldValue: givenNameValue,
          surnameFieldValue: surnameValue,
          primaryPerson: false,
        })

        localDispatches = localDispatches.concat(personDispatches)

        // Update relationship object to use the new person ID
        relationship.personId = newPersonId
      }

      if (!relationship.personId) {
        return localDispatches
      }

      let otherPerson = getNestedOrFloatingElementById(images, relationship.personId)

      // If we just created this person, they won't be in SLS yet, so we construct a minimal object
      if (!otherPerson && relationship.value?.key === 'NEW_PERSON') {
        // We construct a minimal person object from available data sources
        otherPerson = {
          id: relationship.personId, // Source: relationship.personId (newly generated ID)
          partitionKey: person.partitionKey, // Source: person.partitionKey (same partition as primary person)
          recordId: targetRecordId, // Source: targetRecordId (the record we are saving to)
          primary: false,
        }
      }

      // Defensive check: ensure other person exists in SLS
      if (!otherPerson) {
        throw new Error(
          `Cannot find person with ID "${relationship.personId}" in SLS state. ` +
            `This person must exist before creating a relationship.`
        )
      }

      // Check if this is an existing relationship
      const relationshipEl =
        relationship.slsRelationshipId && relationship.relationshipType !== NO_RELATION_TYPE
          ? getNestedOrFloatingElementById(images, relationship.slsRelationshipId)
          : null
      const isExistingRelationship = !!relationshipEl

      if (isExistingRelationship) {
        // CASE 1: Existing relationship
        localDispatches = localDispatches.concat(
          handleExistingRelationship({
            relationship,
            relationshipEl,
            person,
            otherPerson,
            defaultValues: processedDefaultValues,
            images,
            targetRecordId,
            targetRecPartitionKey,
          })
        )
      } else {
        // CASE 2: New relationship
        // If the focused person was just created, it won't have a recordId yet, so we ensure to set it
        const newPerson = { ...person, recordId: person?.recordId || targetRecordId }

        localDispatches = localDispatches.concat(
          handleNewRelationship({
            relationship,
            person: newPerson,
            otherPerson,
            images,
            targetRecordId,
            personMovedToNewRecord,
            targetRecPartitionKey,
          })
        )
      }

      return localDispatches
    })
  )
  dispatches = dispatches.concat(relationshipResults.flat())

  // Every move is queued by now, so ties between people who moved in together can be put back.
  const movedPersons = getPersonsMovingIntoRecord({ relationships, person, images, targetRecordId })
  debug('people moving into the target record', {
    targetRecordId,
    movedPersonIds: movedPersons.map((movedPerson) => movedPerson.id),
  })

  dispatches = dispatches.concat(
    restoreRelationshipsAmongMovedPersons({ movedPersons, partitionKey: person.partitionKey })
  )

  // CASE 3: Handle removed persons from household
  // Include relationships marked as deleted in the form
  const markedFromDeletion = relationships.filter((rel) => rel.isDeleted)
  const removeDispatchesArr = await Promise.all(
    markedFromDeletion.map((relationship) =>
      handleRemovePersonFromHousehold({ relationship, images, currentPartitionKey })
    )
  )
  dispatches = dispatches.concat(removeDispatchesArr.flat())

  // Handle primary status changes
  const primaryStatusChanges = new Map()

  // 1. Check if targetPersonIsPrimary has changed
  const targetPersonIsPrimary = formData.targetPersonIsPrimary
  if (targetPersonIsPrimary !== undefined && targetPersonIsPrimary !== null) {
    const targetPerson = getNestedOrFloatingElementById(images, person.id)
    if (targetPerson && targetPerson.primary !== targetPersonIsPrimary) {
      // Primary status changed for target person
      primaryStatusChanges.set(person.id, {
        personId: person.id,
        isPrimary: targetPersonIsPrimary,
        partitionKey: targetPerson.partitionKey,
      })
    }
  }

  // 2. Collect secondaryPersonPrimaryStatusChange from each relationship
  relationships.forEach((relationship) => {
    if (relationship.isDeleted) {
      return // Skip deleted relationships
    }

    if (
      relationship.secondaryPersonPrimaryStatusChange !== null &&
      relationship.secondaryPersonPrimaryStatusChange !== undefined &&
      relationship.personId
    ) {
      const secondaryPerson = getNestedOrFloatingElementById(images, relationship.personId)

      // For newly created persons, secondaryPerson will be null since they don't exist in SLS yet
      // We need to add SET_PRIMARY for them using the current partition key
      if (!secondaryPerson && relationship.value?.key === 'NEW_PERSON') {
        // New person - add SET_PRIMARY dispatch using current partition key
        primaryStatusChanges.set(relationship.personId, {
          personId: relationship.personId,
          isPrimary: relationship.secondaryPersonPrimaryStatusChange,
          partitionKey: currentPartitionKey, // Use current partition key since new persons are added to current image
        })
      } else if (secondaryPerson && secondaryPerson.primary !== relationship.secondaryPersonPrimaryStatusChange) {
        // Existing person - only add if primary status changed
        primaryStatusChanges.set(relationship.personId, {
          personId: relationship.personId,
          isPrimary: relationship.secondaryPersonPrimaryStatusChange,
          partitionKey: secondaryPerson.partitionKey,
        })
      }
    }
  })

  // Apply the primary status changes
  primaryStatusChanges.forEach((change) => {
    dispatches.push({
      type: 'SET_PRIMARY',
      value: {
        partitionKey: change.partitionKey,
        id: change.personId,
        primary: change.isPrimary,
      },
    })
  })

  // Updating review dispatches with temporay person IDs if needed
  if (reviewDispatches?.length > 0 && newPersonIdsMap.size > 0) {
    reviewDispatches.forEach((dispatch) => {
      if (dispatch.type === 'ADD_SUB') {
        const originalPersonId = dispatch?.value?.superId
        if (newPersonIdsMap.has(originalPersonId)) {
          // Update to the actual new person ID
          dispatch.value.superId = newPersonIdsMap.get(originalPersonId)
        }
      }
    })
  }

  // Remove duplicate dispatches
  const dedupedDispatches = superDeduper([...recordDispatches, ...dispatches, ...reviewDispatches]) // Prepend new record creation dispatches and append review dispatches

  // Move the SET_RECORD_PERSON_IDS dispatch to the end of the array since the deduping function removes all but the first instance
  const setRecordPersonIdsIx = dedupedDispatches.findIndex((d) => d.type === 'SET_RECORD_PERSON_IDS')
  if (setRecordPersonIdsIx !== -1) {
    const removedDispatchArr = dedupedDispatches.splice(setRecordPersonIdsIx, 1)
    dedupedDispatches.push(removedDispatchArr[0])
  }

  return dedupedDispatches
}

/**
 * Extracts person IDs from CREATE actions in a list of dispatches.
 *
 * Iterates through the provided dispatches and collects the IDs of any persons being created.
 * This is useful for identifying new persons that need to be added to a review list.
 *
 * @param {Array<Object>} dispatches Array of SLS dispatch objects.
 * @returns {Array<string>} Array of person IDs created by the dispatches.
 */
export function getPersonIdsToReviewFromCreateActions(dispatches) {
  if (!dispatches || !Array.isArray(dispatches)) {
    return []
  }

  return dispatches
    .filter((dispatch) => dispatch.type === 'CREATE_ELEMENT' && dispatch.value?.elementType === 'PERSON')
    .map((dispatch) => dispatch.value?.id)
    .filter(Boolean)
}
